import { useState, useEffect, useCallback, useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Button } from '../../components/ui/button';
import { Form } from '../../components/ui/form';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Progress } from '../../components/ui/progress';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../../components/ui/pagination';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader,
  DialogTitle, DialogTrigger, DialogFooter,
} from '../../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import {
  Search, Plus, Filter, MoreHorizontal, Eye, EyeOff, Edit, Trash2,
  Download, Phone, Mail, Star, Target, RefreshCw, UserPlus, AlertCircle,
  Shield,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  EditAgentModal, DeleteModal,
} from '../../components/ui/ActionModals';
import { useAuth } from '../../contexts/AuthContext';
import {
  getAgents,
  createAgent,
  deleteAgent,
  getEmployees,
  getAgentRequests,
  updateAgentRequest,
  bulkUpdateAgentRequests,
} from '../../lib/api';
import { cn } from '../../lib/utils';
import {
  AGENT_DEFAULT_VALUES,
  AGENT_STATUS_OPTIONS,
  agentCreateSchema,
  buildAgentCreatePayload,
  getEntityErrorMessage,
  mapEntityApiErrors,
} from '../../lib/entityFormValidation';
import { canImpersonateUser, getDashboardPathForRole } from '../../lib/auth';

const statusColors = {
  Active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300',
  Inactive: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
};

const statusApiToDisplay = (s) => {
  if (!s) return 'Active';
  const v = String(s).toLowerCase();
  if (v === 'inactive' || v === 'suspended') return 'Inactive';
  return 'Active';
};

const isAgentPlanLimitError = (message) =>
  typeof message === 'string' && message.toLowerCase().includes('agent limit reached for your plan');

const buildPaginationItems = (page, totalPages) => {
  const pages = [];
  for (let current = 1; current <= totalPages; current += 1) {
    if (current === 1 || current === totalPages || Math.abs(current - page) <= 1) {
      pages.push(current);
    } else if (pages[pages.length - 1] !== '...') {
      pages.push('...');
    }
  }
  return pages;
};

function TablePagination({ page, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;
  const items = buildPaginationItems(page, totalPages);
  return (
    <Pagination className="justify-end">
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            href="#"
            onClick={(event) => {
              event.preventDefault();
              if (page > 1) onPageChange(page - 1);
            }}
            className={page <= 1 ? 'pointer-events-none opacity-50' : ''}
          />
        </PaginationItem>
        {items.map((item, index) => (
          <PaginationItem key={`${item}-${index}`}>
            {item === '...' ? (
              <PaginationEllipsis />
            ) : (
              <PaginationLink
                href="#"
                isActive={item === page}
                onClick={(event) => {
                  event.preventDefault();
                  onPageChange(item);
                }}
              >
                {item}
              </PaginationLink>
            )}
          </PaginationItem>
        ))}
        <PaginationItem>
          <PaginationNext
            href="#"
            onClick={(event) => {
              event.preventDefault();
              if (page < totalPages) onPageChange(page + 1);
            }}
            className={page >= totalPages ? 'pointer-events-none opacity-50' : ''}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}

const AgentsTable = ({ agents, title, description }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = getRoleBasePath(location.pathname);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [modal, setModal] = useState(null); // 'view' | 'edit' | 'status' | 'delete'
  const [apiAgents, setApiAgents] = useState(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(false);
  const [impersonatingId, setImpersonatingId] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const addAgentForm = useForm({
    resolver: zodResolver(agentCreateSchema),
    defaultValues: AGENT_DEFAULT_VALUES,
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });
  const addForm = addAgentForm.watch();
  const setAddForm = (updater) => {
    const currentValues = addAgentForm.getValues();
    const nextValues = typeof updater === 'function' ? updater(currentValues) : updater;
    Object.entries(nextValues).forEach(([key, value]) => {
      addAgentForm.setValue(key, value, {
        shouldDirty: true,
        shouldValidate: true,
      });
    });
  };
  const [showPassword, setShowPassword] = useState(false);
  const { token, user, startImpersonation } = useAuth();
  const canManageAgents = user?.role === 'super_admin'
    || user?.role === 'wl_admin'
    || (user?.role === 'employee' && Array.isArray(user?.permissions) && user.permissions.includes('agents.edit'));
  const canImpersonateAgents = user?.role === 'super_admin'
    || user?.role === 'wl_admin'
    || user?.role === 'employee';
  const canDeleteAgents = user?.role !== 'wl_admin';

  const openModal = (agent, type) => { setSelectedAgent(agent); setModal(type); };
  const closeModal = () => { setModal(null); setSelectedAgent(null); };

  const fetchAgents = useCallback(async () => {
    if (!token) {
      setApiAgents([]);
      setPagination({ page: 1, pageSize: 10, total: 0, totalPages: 1 });
      setError('Unable to load data.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const resp = await getAgents(token, {
        q: searchTerm.trim() || undefined,
        status: statusFilter !== 'all' ? statusFilter.toLowerCase() : undefined,
        page,
        pageSize: 10,
      });
      const items = resp?.data?.items ?? [];
      const mapped = items.map((u) => ({
        id: u.id,
        name: u.full_name,
        email: u.email,
        role: 'agent',
        phone: u.phone || '—',
        createdById: u.created_by_id ?? '',
        whiteLabelId: u.white_label_id ?? null,
        createdLoanApplications: Number(u.created_loan_applications_count) || 0,
        totalLeads: Number(u.created_leads_count) || 0,
        convertedLeads: Number(u.converted_leads_count) || 0,
        achieved: Number(u.converted_leads_count) || 0,
        target: Number(u.target) || 0,
        commissionEarned: 0,
        pendingCommission: 0,
        rating: 5,
        status: statusApiToDisplay(u.status),
        createdBy: u.created_by_name || 'Admin',
        joinDate: u.created_at
          ? new Date(u.created_at).toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })
          : '—',
      }));
      setApiAgents(mapped);
      const meta = resp?.data?.pagination || {};
      setPagination({
        page: Number(meta.page || page),
        pageSize: Number(meta.page_size || 10),
        total: Number(meta.total || items.length || 0),
        totalPages: Number(meta.total_pages || 1),
      });
    } catch {
      setApiAgents([]);
      setPagination({ page: 1, pageSize: 10, total: 0, totalPages: 1 });
      setError('Unable to load data.');
    } finally {
      setLoading(false);
    }
  }, [token, page, searchTerm, statusFilter]);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, statusFilter]);

  useEffect(() => {
    const subscription = addAgentForm.watch(() => {
      if (submitError) {
        setSubmitError('');
      }
    });
    return () => subscription.unsubscribe();
  }, [addAgentForm, submitError]);

  const agentsToShow = apiAgents ?? agents;

  const filteredAgents = agentsToShow;

  const handleImpersonateAgent = async (agent) => {
    setImpersonatingId(agent.id);
    try {
      const result = await startImpersonation(agent.id);
      if (!result?.success || !result?.user) {
        throw new Error(result?.error || 'Failed to start impersonation');
      }
      window.location.assign(getDashboardPathForRole(result.user.role));
    } catch (err) {
      toast.error(err?.message || 'Failed to start impersonation');
    } finally {
      setImpersonatingId('');
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);

  const handleAddAgent = async (values) => {
    if (!canManageAgents) {
      toast.error('You do not have permission to create agents');
      return;
    }
    if (!token) {
      toast.success('Agent added locally (no API)');
      setShowAddDialog(false);
      return;
    }
    setSubmitError('');
    setSubmitting(true);
    try {
      const resp = await createAgent(token, buildAgentCreatePayload(values));
      toast.success('Agent added successfully!');
      const created = resp?.data;
      if (created) {
        const newAgent = {
          id: created.id,
          name: created.full_name,
          email: created.email,
          phone: created.phone || '—',
          createdLoanApplications: Number(created.created_loan_applications_count) || 0,
          totalLeads: Number(created.created_leads_count) || 0,
          convertedLeads: Number(created.converted_leads_count) || 0,
          achieved: Number(created.converted_leads_count) || 0,
          target: Number(created.target) || 0,
          commissionEarned: 0,
          pendingCommission: 0,
          rating: 5,
          status: statusApiToDisplay(created.status),
          joinDate: created.created_at
            ? new Date(created.created_at).toLocaleDateString('en-IN', {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
            })
            : '—',
        };
        setApiAgents((prev) => [newAgent, ...(prev || [])]);
      }
      setShowAddDialog(false);
      addAgentForm.reset(AGENT_DEFAULT_VALUES);
      fetchAgents()
    } catch (err) {
      const fieldErrors = mapEntityApiErrors(err);
      Object.entries(fieldErrors).forEach(([fieldName, message]) => {
        addAgentForm.setError(fieldName, { type: 'server', message });
      });
      const firstField = Object.keys(fieldErrors)[0];
      if (firstField) {
        addAgentForm.setFocus(firstField);
      }

      const message = getEntityErrorMessage(err, 'Failed to add agent.');
      setSubmitError(message);
      if (user?.role === 'wl_admin' && isAgentPlanLimitError(message)) {
        toast.error(message, {
          style: {
            zIndex: 999999,
          },
          action: {
            label: 'Upgrade Plan',
            onClick: () => navigate('/whitelabel/upgrade-plan'),
          },
        });
      } else {
        toast.error(message, {
          style: {
            zIndex: 999999,
          },
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="agents-management">
      {/* Production Modals */}
      <EditAgentModal
        agent={selectedAgent}
        open={modal === 'edit'}
        onClose={closeModal}
        token={token}
        onSaved={(updated) => {
          if (!updated) return;
          const mapped = {
            id: updated.id,
            name: updated.full_name,
            email: updated.email,
            phone: updated.phone || '—',
            createdLoanApplications: selectedAgent?.createdLoanApplications ?? 0,
            totalLeads: selectedAgent?.totalLeads ?? 0,
            convertedLeads: selectedAgent?.convertedLeads ?? 0,
            achieved: selectedAgent?.achieved ?? 0,
            target: Number(updated.target) || 0,
            commissionEarned: selectedAgent?.commissionEarned ?? 0,
            pendingCommission: selectedAgent?.pendingCommission ?? 0,
            rating: selectedAgent?.rating ?? 5,
            status: statusApiToDisplay(updated.status),
            createdBy: updated.created_by_name || selectedAgent?.createdBy || 'Admin',
            joinDate: updated.created_at
              ? new Date(updated.created_at).toLocaleDateString('en-IN', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })
              : selectedAgent?.joinDate || '—',
          };
          setApiAgents((prev) => (prev || []).map((a) => (a.id === mapped.id ? { ...a, ...mapped } : a)));
        }}
      />
      <DeleteModal
        open={modal === 'delete'}
        onClose={closeModal}
        title={selectedAgent?.name}
        description={`Are you sure you want to delete agent "${selectedAgent?.name}"? All their leads, commission data and targets will be permanently removed.`}
        onConfirm={async () => {
          if (!canDeleteAgents) return;
          if (!token || !selectedAgent?.id) {
            throw new Error('Please login again and retry.');
          }
          await deleteAgent(token, selectedAgent.id);
          setApiAgents((prev) => (prev || []).filter((a) => a.id !== selectedAgent.id));
        }}
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Plus_Jakarta_Sans']">
            {title || 'Agents Management'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {description || 'Manage your DSA agents and track their performance'}
          </p>
        </div>
        <Dialog open={showAddDialog} onOpenChange={(open) => {
          setShowAddDialog(open);
          if (open) {
            addAgentForm.reset(AGENT_DEFAULT_VALUES);
            setSubmitError('');
            setShowPassword(false);
          }
        }}>
          {canManageAgents ? (
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-agent-btn">
                <Plus className="h-4 w-4 mr-2" />Add Agent
              </Button>
            </DialogTrigger>
          ) : null}
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Add New Agent</DialogTitle>
              <DialogDescription>Enter the agent details below.</DialogDescription>
            </DialogHeader>
            <Form {...addAgentForm}>
              <form onSubmit={addAgentForm.handleSubmit(handleAddAgent)} className="space-y-4" noValidate>
              {submitError ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{submitError}</AlertDescription>
                </Alert>
              ) : null}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    placeholder="Enter name"
                    value={addForm.name}
                    onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))}
                    required
                  />
                  {addAgentForm.formState.errors.name ? <p className="text-[0.8rem] font-medium text-destructive">{addAgentForm.formState.errors.name.message}</p> : null}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    placeholder="Enter phone"
                    value={addForm.phone}
                    onChange={(e) => setAddForm((f) => ({ ...f, phone: e.target.value }))}
                    required
                  />
                  {addAgentForm.formState.errors.phone ? <p className="text-[0.8rem] font-medium text-destructive">{addAgentForm.formState.errors.phone.message}</p> : null}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter email"
                  value={addForm.email}
                  onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))}
                  required
                />
                {addAgentForm.formState.errors.email ? <p className="text-[0.8rem] font-medium text-destructive">{addAgentForm.formState.errors.email.message}</p> : null}
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Use a strong password"
                    value={addForm.password}
                    onChange={(e) => setAddForm((f) => ({ ...f, password: e.target.value }))}
                    required
                    className="pr-10"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    tabIndex="-1"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {addAgentForm.formState.errors.password ? <p className="text-[0.8rem] font-medium text-destructive">{addAgentForm.formState.errors.password.message}</p> : null}
              </div>
              <div className="space-y-2">
                <Label htmlFor="target">Monthly Target</Label>
                <Input
                  id="target"
                  type="number"
                  placeholder="Enter target"
                  value={addForm.target}
                  onChange={(e) => setAddForm((f) => ({ ...f, target: e.target.value }))}
                />
                {addAgentForm.formState.errors.target ? <p className="text-[0.8rem] font-medium text-destructive">{addAgentForm.formState.errors.target.message}</p> : null}
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={addForm.status}
                  onValueChange={(v) => setAddForm((f) => ({ ...f, status: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AGENT_STATUS_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {addAgentForm.formState.errors.status ? <p className="text-[0.8rem] font-medium text-destructive">{addAgentForm.formState.errors.status.message}</p> : null}
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)} disabled={submitting}>Cancel</Button>
                <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" disabled={submitting}>
                  {submitting ? 'Saving...' : 'Save Agent'}
                </Button>
              </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="border border-border">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-agents-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="agents-status-filter">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filter status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border border-border">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Agent</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Loan Applications</TableHead>
                  <TableHead>Total Leads</TableHead>
                  <TableHead>Converted</TableHead>
                  <TableHead>Target Progress</TableHead>
                  <TableHead>Commission Earned</TableHead>
                  <TableHead>Pending</TableHead>
                  {/* <TableHead>Rating</TableHead> */}
                  {(user?.role === 'super_admin' || user?.role === 'wl_admin') && (
                    <TableHead>Created By</TableHead>
                  )}
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!loading && !error && filteredAgents.map((agent) => (
                  <TableRow key={agent.id} data-testid={`agent-row-${agent.id}`} className="hover:bg-muted/30 transition-colors">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-9 w-9">
                          <AvatarFallback>
                            {agent.name.split(' ').map((n) => n[0]).join('')}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{agent.name}</p>
                          <p className="text-xs text-muted-foreground">Joined {agent.joinDate}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <span className="flex items-center gap-1 text-sm">
                          <Phone className="h-3 w-3 text-muted-foreground" />{agent.phone}
                        </span>
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Mail className="h-3 w-3" />{agent.email}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{agent.createdLoanApplications ?? 0}</TableCell>
                    <TableCell className="font-medium">{agent.totalLeads}</TableCell>
                    <TableCell className="font-medium text-emerald-600">{agent.convertedLeads}</TableCell>
                    <TableCell>
                      <div className="w-24">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs">{agent.achieved}/{agent.target}</span>
                        </div>
                        <Progress value={agent.target > 0 ? (agent.achieved / agent.target) * 100 : 0} className="h-2" />
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{formatCurrency(agent.commissionEarned)}</TableCell>
                    <TableCell className="text-amber-600">{formatCurrency(agent.pendingCommission)}</TableCell>
                    {/* <TableCell>
                      <div className="flex items-center gap-1">
                        <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                        <span className="font-medium">{agent.rating}</span>
                      </div>
                    </TableCell> */}
                    {(user?.role === 'super_admin' || user?.role === 'wl_admin') && (
                      <TableCell>
                        <Badge variant="outline" className={cn(
                          "font-normal",
                          agent.createdBy === 'Admin'
                            ? "border-emerald-200 text-emerald-700 bg-emerald-50/50"
                            : "border-blue-200 text-blue-700 bg-blue-50/50"
                        )}>
                          {agent.createdBy}
                        </Badge>
                      </TableCell>
                    )}
                    <TableCell>
                      <Badge className={statusColors[agent.status]}>{agent.status}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem onClick={() => navigate(`${basePath}/agents/${agent.id}/view`)} className="gap-2 cursor-pointer">
                            <Eye className="h-4 w-4 text-blue-500" /><span>View Profile</span>
                          </DropdownMenuItem>
                          {canImpersonateAgents && canImpersonateUser(user, agent) ? (
                            <DropdownMenuItem
                              onClick={() => handleImpersonateAgent(agent)}
                              className="gap-2 cursor-pointer"
                              disabled={impersonatingId === agent.id}
                            >
                              <Shield className="h-4 w-4 text-emerald-600" />
                              <span>{impersonatingId === agent.id ? 'Starting Session...' : 'Impersonate'}</span>
                            </DropdownMenuItem>
                          ) : null}
                          {canManageAgents ? (
                            <DropdownMenuItem onClick={() => openModal(agent, 'edit')} className="gap-2 cursor-pointer">
                              <Edit className="h-4 w-4 text-amber-500" /><span>Edit Agent</span>
                            </DropdownMenuItem>
                          ) : null}
                        
                          {canManageAgents && canDeleteAgents ? (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => openModal(agent, 'delete')}
                                className="gap-2 cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-950"
                              >
                                <Trash2 className="h-4 w-4" /><span>Delete Agent</span>
                              </DropdownMenuItem>
                            </>
                          ) : null}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {loading && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Loading...</p>
            </div>
          )}
          {!loading && error && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Unable to load data.</p>
            </div>
          )}
          {!loading && !error && filteredAgents.length === 0 && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">
                {agentsToShow.length === 0 ? 'No records available.' : 'No matching records found.'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">
          Showing page {pagination.page} of {pagination.totalPages} · {pagination.total} agents
        </p>
        <TablePagination page={page} totalPages={pagination.totalPages} onPageChange={setPage} />
      </div>
    </div>
  );
};

const agentRequestStatusOptions = [
  { value: 'pending', label: 'Pending' },
  { value: 'in_review', label: 'In Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

const agentRequestStatusColors = {
  Pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
  'In Review': 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  Approved: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300',
  Rejected: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
};

const agentRequestStatusToDisplay = (value) => {
  if (value === 'in_review') return 'In Review';
  if (value === 'approved') return 'Approved';
  if (value === 'rejected') return 'Rejected';
  return 'Pending';
};

const agentRequestDisplayToApi = (value) => {
  if (value === 'In Review') return 'in_review';
  return value.toLowerCase();
};

const mapAgentRequest = (item) => ({
  id: item.id,
  name: item.full_name,
  email: item.email,
  phone: item.phone,
  city: item.city,
  experienceYears: Number(item.experience_years) || 0,
  affiliations: item.affiliations || '—',
  notes: item.notes || '',
  status: agentRequestStatusToDisplay(item.status),
  assignedEmployeeId: item.assigned_employee_id || '',
  assignedEmployeeName: item.assigned_employee_name || 'Unassigned',
  reviewedByName: item.reviewed_by_name || '—',
  sourcePage: item.source_page || '/become-agent',
  requestedOn: item.created_at
    ? new Date(item.created_at).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
    : '—',
});

const ViewAgentRequestDialog = ({ requestItem, open, onClose }) => {
  if (!requestItem) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>Agent Request Details</DialogTitle>
          <DialogDescription>{requestItem.id}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-xl border p-4">
            <div>
              <p className="text-lg font-semibold">{requestItem.name}</p>
              <p className="text-sm text-muted-foreground">{requestItem.email}</p>
            </div>
            <Badge className={agentRequestStatusColors[requestItem.status]}>{requestItem.status}</Badge>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="rounded-lg border p-3">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Phone</p>
              <p className="mt-1 text-sm font-medium">{requestItem.phone}</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">City</p>
              <p className="mt-1 text-sm font-medium">{requestItem.city}</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Experience</p>
              <p className="mt-1 text-sm font-medium">{requestItem.experienceYears} years</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Assigned Employee</p>
              <p className="mt-1 text-sm font-medium">{requestItem.assignedEmployeeName}</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Requested On</p>
              <p className="mt-1 text-sm font-medium">{requestItem.requestedOn}</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Reviewed By</p>
              <p className="mt-1 text-sm font-medium">{requestItem.reviewedByName}</p>
            </div>
          </div>

          <div className="rounded-lg border p-3">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Current Affiliations</p>
            <p className="mt-1 text-sm leading-6">{requestItem.affiliations}</p>
          </div>

          <div className="rounded-lg border p-3">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Admin Notes</p>
            <p className="mt-1 text-sm leading-6">{requestItem.notes || '—'}</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const getRoleBasePath = (pathname) => {
  if (pathname.startsWith('/whitelabel')) return '/whitelabel';
  if (pathname.startsWith('/employee')) return '/employee';
  return '/admin';
};

const ManageAgentRequestDialog = ({
  requestItem,
  open,
  onClose,
  token,
  employees,
  onSaved,
}) => {
  const [form, setForm] = useState({
    status: 'Pending',
    assignedEmployeeId: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (requestItem) {
      setForm({
        status: requestItem.status || 'Pending',
        assignedEmployeeId: requestItem.assignedEmployeeId || '',
        notes: requestItem.notes || '',
      });
    }
  }, [requestItem]);

  if (!requestItem) return null;

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const resp = await updateAgentRequest(token, requestItem.id, {
        status: agentRequestDisplayToApi(form.status),
        assigned_employee_id: form.assignedEmployeeId || '',
        notes: form.notes.trim() || null,
      });
      toast.success(resp?.message || 'Request updated successfully');
      onSaved(resp?.data || null);
      onClose();
    } catch (err) {
      toast.error(err?.message || 'Failed to update request');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Manage Agent Request</DialogTitle>
          <DialogDescription>{requestItem.name} • {requestItem.email}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSave} className="space-y-4">
          <div className="space-y-2">
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(value) => setForm((prev) => ({ ...prev, status: value }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {agentRequestStatusOptions.map((option) => (
                  <SelectItem key={option.value} value={option.label}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Assign Employee</Label>
            <Select
              value={form.assignedEmployeeId || '_none'}
              onValueChange={(value) => setForm((prev) => ({ ...prev, assignedEmployeeId: value === '_none' ? '' : value }))}
            >
              <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">Unassigned</SelectItem>
                {employees.map((employee) => (
                  <SelectItem key={employee.id} value={employee.id}>{employee.full_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              rows={4}
              value={form.notes}
              onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
              placeholder="Add internal notes for this request"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const AgentsRequestTable = ({ agents, title, description }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [modal, setModal] = useState(null);
  const [selectedRequestIds, setSelectedRequestIds] = useState([]);
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkAssignedEmployeeId, setBulkAssignedEmployeeId] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { token, user } = useAuth();
  const canManageRequests = user?.role === 'super_admin';

  const openModal = (requestItem, type) => {
    setSelectedRequest(requestItem);
    setModal(type);
  };

  const closeModal = () => {
    setModal(null);
    setSelectedRequest(null);
  };

  const fetchRequests = useCallback(async () => {
    if (!token || !canManageRequests) {
      setRows([]);
      setError('Unable to load data.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const resp = await getAgentRequests(token);
      const items = resp?.data?.items ?? [];
      setRows(items.map(mapAgentRequest));
    } catch (err) {
      setRows([]);
      setError(err?.message || 'Unable to load data.');
    } finally {
      setLoading(false);
    }
  }, [token, canManageRequests]);

  const fetchEmployeesList = useCallback(async () => {
    if (!token || !canManageRequests) {
      setEmployees([]);
      return;
    }
    try {
      const resp = await getEmployees(token);
      const items = resp?.data?.items ?? [];
      setEmployees(items.filter((item) => !item.white_label_id));
    } catch {
      setEmployees([]);
    }
  }, [token, canManageRequests]);

  useEffect(() => {
    fetchRequests();
    fetchEmployeesList();
  }, [fetchRequests, fetchEmployeesList]);

  useEffect(() => {
    setSelectedRequestIds((prev) => prev.filter((id) => rows.some((row) => row.id === id)));
  }, [rows]);

  const filteredRows = useMemo(() => rows.filter((item) => {
    const query = searchTerm.toLowerCase();
    const matchesSearch = [item.name, item.email, item.phone, item.city]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(query));
    const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
    return matchesSearch && matchesStatus;
  }), [rows, searchTerm, statusFilter]);

  const clearSelection = () => {
    setSelectedRequestIds([]);
    setBulkStatus('');
    setBulkAssignedEmployeeId('');
  };

  const toggleSelection = (requestId, checked) => {
    setSelectedRequestIds((prev) => {
      const exists = prev.includes(requestId);
      if (checked) return exists ? prev : [...prev, requestId];
      return exists ? prev.filter((id) => id !== requestId) : prev;
    });
  };

  const allRowsSelected = filteredRows.length > 0 && filteredRows.every((row) => selectedRequestIds.includes(row.id));

  const updateRowFromResponse = (updated) => {
    if (!updated) return;
    if (updated.converted_to_agent && updated.request_id) {
      setRows((prev) => prev.filter((item) => item.id !== updated.request_id));
      setSelectedRequestIds((prev) => prev.filter((id) => id !== updated.request_id));
      return;
    }
    const mapped = mapAgentRequest(updated);
    setRows((prev) => prev.map((item) => (item.id === mapped.id ? mapped : item)));
  };

  const handleQuickUpdate = async (requestId, payload, successMessage) => {
    try {
      const resp = await updateAgentRequest(token, requestId, payload);
      updateRowFromResponse(resp?.data || null);
      toast.success(resp?.message || successMessage);
    } catch (err) {
      toast.error(err?.message || 'Failed to update request');
    }
  };

  const applyBulkUpdate = async () => {
    if (!token || selectedRequestIds.length === 0 || (!bulkStatus && bulkAssignedEmployeeId === '')) return;
    setBulkSaving(true);
    try {
      const payload = {
        request_ids: selectedRequestIds,
      };
      if (bulkStatus) payload.status = bulkStatus;
      if (bulkAssignedEmployeeId || bulkAssignedEmployeeId === '_unassigned') {
        payload.assigned_employee_id = bulkAssignedEmployeeId === '_unassigned' ? '' : bulkAssignedEmployeeId;
      }
      const resp = await bulkUpdateAgentRequests(token, payload);
      toast.success(resp?.message || 'Requests updated');
      clearSelection();
      fetchRequests();
    } catch (err) {
      toast.error(err?.message || 'Failed to update requests');
    } finally {
      setBulkSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="agent-requests-management">
      <ViewAgentRequestDialog requestItem={selectedRequest} open={modal === 'view'} onClose={closeModal} />
      <ManageAgentRequestDialog
        requestItem={selectedRequest}
        open={modal === 'manage'}
        onClose={closeModal}
        token={token}
        employees={employees}
        onSaved={updateRowFromResponse}
      />

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Plus_Jakarta_Sans']">
            {title || 'Agent Request'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {description || 'Review public become-agent applications'}
          </p>
        </div>
        <Button variant="outline" onClick={fetchRequests} disabled={loading}>
          <RefreshCw className={cn('h-4 w-4 mr-2', loading ? 'animate-spin' : '')} />
          Refresh
        </Button>
      </div>

      <Card className="border border-border">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, email, phone or city..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-agent-requests-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="agent-requests-status-filter">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filter status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {agentRequestStatusOptions.map((option) => (
                  <SelectItem key={option.value} value={option.label}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          
          </div>
        </CardContent>
      </Card>

      {selectedRequestIds.length > 0 && (
        <Card className="border-2 border-emerald-500/20 bg-emerald-50/50 dark:bg-emerald-950/10 shadow-sm overflow-hidden">
          <CardContent className="p-4 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-sm font-bold text-foreground">{selectedRequestIds.length} Requests Selected</p>
              <p className="text-[11px] text-muted-foreground">Bulk approve, reject, update status or assign employee</p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Select value={bulkStatus || '_none'} onValueChange={(value) => setBulkStatus(value === '_none' ? '' : value)}>
                <SelectTrigger className="w-[160px] h-9 bg-background border-emerald-500/20" disabled={bulkSaving}>
                  <SelectValue placeholder="Update Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Keep Status</SelectItem>
                  {agentRequestStatusOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={bulkAssignedEmployeeId || '_unchanged'}
                onValueChange={(value) => setBulkAssignedEmployeeId(value === '_unchanged' ? '' : value)}
              >
                <SelectTrigger className="w-[190px] h-9 bg-background border-emerald-500/20" disabled={bulkSaving}>
                  <SelectValue placeholder="Assign Employee" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_unchanged">Keep Assignment</SelectItem>
                  <SelectItem value="_unassigned">Unassigned</SelectItem>
                  {employees.map((employee) => (
                    <SelectItem key={employee.id} value={employee.id}>{employee.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                onClick={applyBulkUpdate}
                disabled={bulkSaving || (!bulkStatus && bulkAssignedEmployeeId === '')}
                className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 px-5 font-semibold"
              >
                {bulkSaving ? 'Saving...' : 'Apply Changes'}
              </Button>

              <Button variant="ghost" size="sm" onClick={clearSelection} disabled={bulkSaving} className="h-9">
                Close
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border border-border">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={allRowsSelected}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedRequestIds(filteredRows.map((row) => row.id));
                          return;
                        }
                        clearSelection();
                      }}
                      aria-label="Select all requests"
                    />
                  </TableHead>
                  <TableHead>Applicant</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>City</TableHead>
                  <TableHead>Experience</TableHead>
                  <TableHead>Assigned To</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Requested On</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!loading && !error && filteredRows.map((requestItem) => (
                  <TableRow key={requestItem.id} data-testid={`agent-request-row-${requestItem.id}`} className="hover:bg-muted/30 transition-colors">
                    <TableCell className="w-12">
                      <Checkbox
                        checked={selectedRequestIds.includes(requestItem.id)}
                        onCheckedChange={(checked) => toggleSelection(requestItem.id, Boolean(checked))}
                        aria-label={`Select request ${requestItem.id}`}
                      />
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{requestItem.name}</p>
                        <p className="text-xs text-muted-foreground line-clamp-1">{requestItem.affiliations}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <span className="flex items-center gap-1 text-sm">
                          <Phone className="h-3 w-3 text-muted-foreground" />{requestItem.phone}
                        </span>
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Mail className="h-3 w-3" />{requestItem.email}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>{requestItem.city}</TableCell>
                    <TableCell>{requestItem.experienceYears} yrs</TableCell>
                    <TableCell>{requestItem.assignedEmployeeName}</TableCell>
                    <TableCell>
                      <Badge className={agentRequestStatusColors[requestItem.status]}>{requestItem.status}</Badge>
                    </TableCell>
                    <TableCell>{requestItem.requestedOn}</TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-52">
                          <DropdownMenuItem onClick={() => openModal(requestItem, 'view')} className="gap-2 cursor-pointer">
                            <Eye className="h-4 w-4 text-blue-500" /><span>View Details</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openModal(requestItem, 'manage')} className="gap-2 cursor-pointer">
                            <Edit className="h-4 w-4 text-amber-500" /><span>Manage Request</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleQuickUpdate(requestItem.id, { status: 'approved' }, 'Request approved')}
                            className="gap-2 cursor-pointer"
                          >
                            <UserPlus className="h-4 w-4 text-emerald-500" /><span>Approve</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleQuickUpdate(requestItem.id, { status: 'rejected' }, 'Request rejected')}
                            className="gap-2 cursor-pointer text-red-600 focus:text-red-600"
                          >
                            <Trash2 className="h-4 w-4" /><span>Reject</span>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {loading && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Loading...</p>
            </div>
          )}
          {!loading && error && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">{error}</p>
            </div>
          )}
          {!loading && !error && filteredRows.length === 0 && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">
                {rows.length === 0 ? 'No requests available.' : 'No matching requests found.'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ─── Exported pages ─────────────────────────────────────────────────────────
export const AllAgents = () => <AgentsTable agents={[]} />;

export const AgentPerformance = () => (
  <AgentsTable
    agents={[]}
    title="Agent Performance"
    description="View agent performance metrics and rankings"
  />
);

export const AgentCommission = () => (
  <AgentsTable
    agents={[]}
    title="Commission Report"
    description="View agent commission earnings and payouts"
  />
);

export const AgentTargets = () => (
  <AgentsTable
    agents={[]}
    title="Target Setup"
    description="Configure and track agent targets"
  />
);

export const AgentRequest = () => (
  <AgentsRequestTable
    agents={[]}
    title="Agent Request"
    description="View agent request"
  />
);


export default AllAgents;
